import { t } from "@lingui/core/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useCallback, useMemo, useState } from "react";
import { useFragment } from "react-relay";

import type { PatronRequestRecurrenceInput } from "@/features/BookingWizard/hooks/useUpsertPatronRequestSeries/__generated__/useUpsertPatronRequestSeriesMutation.graphql";
import { useUpsertPatronRequestSeries } from "@/features/BookingWizard/hooks/useUpsertPatronRequestSeries/useUpsertPatronRequestSeries";
import { useLoyaltyEnrollmentPreference } from "@/features/Loyalty/components/LoyaltyEnrollmentPreference/LoyaltyEnrollmentPreference";
import { RecurrenceAvailabilityPreview } from "@/features/RecurringRequests/types";
import { FacilityApplePayButton } from "@/features/Square/ApplePay";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { Button } from "@whoosh/design-system/src/components/controls";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { VStack, HStack } from "@whoosh/design-system/src/components/primitive";
import { useGetBookingRestrictionStatuses } from "@whoosh/features/src/Restrictions/hooks/useGetBookingRestrictionStatuses/useGetBookingRestrictionStatuses";

import { useUnlockAllSlots } from "../../../../../../../features/LockSlots";
import { SlotLockCountdownTimer } from "../../../../../../../features/LockSlots/components/SlotLockCountdownTimer/SlotLockCountdownTimer";
import { isPaymentMethodEntryModalOpenAtom } from "../../atoms/stores";
import {
  BOOKING_SUMMARY_CONTENT_MAX_WIDTH,
  useIsBookingSummaryNarrow,
} from "../../bookingSummaryWidth";
import { useConfirmDraftBooking } from "../../hooks/useConfirmDraftBooking";
import { useConfirmDraftBookings } from "../../hooks/useConfirmDraftBookings";
import { useOpenConfirmedBookingModalAfterCompletedBooking } from "../../hooks/useOpenConfirmedBookingModalAfterCompletedBooking";
import { useOpenNudgeModalAfterCompletedBooking } from "../../hooks/useOpenNudgeModalAfterCompletedBooking";
import { useSubmitPatronRequestSeries } from "../../hooks/useSubmitPatronRequestSeries";
import { AggregateRequestErrorBanner } from "../BookingRequest/AggregateRequestErrorBanner/AggregateRequestErrorBanner";
import { useHasBlockingRequestRestrictionStatus } from "../RequestRestrictionBannerStack/hooks/useRequestRestrictionStatuses";
import { BookingReservation_Booking$key } from "./__generated__/BookingReservation_Booking.graphql";
import { getButtonText } from "./utils/getButtonText";

type Props = {
  handleGoBack: () => void;
  bookingFragmentRef: BookingReservation_Booking$key | null;
  currentServerTime?: string | null;
  membershipTypeId: string;
  isMemberCaller?: boolean;
  hasPublicSeenNotificationNudgeModal?: boolean;
  isMultipleBooking?: boolean;
  bookingIds?: string[];
  onRequestConfirm?: (proceed: () => void) => void;
  recurrenceAvailabilityPreview?: RecurrenceAvailabilityPreview | null;
  isBookingOwner?: boolean;
  requestGroupCount?: number;
};

export const BookingReservation = ({
  handleGoBack,
  bookingFragmentRef,
  currentServerTime,
  membershipTypeId,
  hasPublicSeenNotificationNudgeModal,
  isMultipleBooking,
  bookingIds,
  onRequestConfirm,
  recurrenceAvailabilityPreview = null,
  isBookingOwner = false,
  requestGroupCount = 1,
}: Props) => {
  const booking = useFragment(
    BookingReservationFragmentString,
    bookingFragmentRef
  );
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const setShouldModalBeOpen = useSetAtom(isPaymentMethodEntryModalOpenAtom);
  const [unlockAllSlots] = useUnlockAllSlots();
  const { showToast } = useToast();

  const trackEvent = useTrackEvent();

  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();

  const currentRequestBookingId =
    route.params?.params?.currentRequestBookingId ?? null;
  const pendingRecurrence = route.params?.params?.pendingRecurrence ?? null;

  const bookingId = booking?.id;
  const { applePay, patronLoyalty } = useFlags();
  const loyaltyEnrollmentPreference = useLoyaltyEnrollmentPreference();
  const hasLoyaltyEnrollmentOffer =
    booking?.patronOrders?.edges?.[0]?.node?.loyaltyEnrollmentOffer != null;
  const isDraftBooking = booking?.status === "DRAFT";
  const hasPersistedRequestSeries =
    booking?.bookingSeries?.kind === "PATRON_REQUEST";
  const hasPendingConfirmedRecurrence =
    booking?.status === "CONFIRMED" &&
    booking?.startSlot?.type === "QUEUE" &&
    !!pendingRecurrence;
  const showConfirmAction = isDraftBooking;
  const clubSupportsPrivateBookings =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate ?? false;

  const isLessonBooking = booking?.__typename === "LessonBooking";
  const isRequestBooking = booking?.startSlot?.type === "QUEUE";
  const isStandaloneRequestPreview =
    !hasPersistedRequestSeries &&
    recurrenceAvailabilityPreview?.included.length === 1 &&
    recurrenceAvailabilityPreview.included[0]?.date ===
      booking?.startSlot?.agenda?.date;
  const isPatronRecurringRequest =
    !isLessonBooking &&
    isRequestBooking &&
    clubSupportsPrivateBookings &&
    !isStandaloneRequestPreview &&
    (!!pendingRecurrence || hasPersistedRequestSeries);
  const hasIncludedRecurrenceOccurrence =
    (recurrenceAvailabilityPreview?.included.length ?? 0) > 0;
  const isPendingRecurrenceSubmissionDisabled =
    !!pendingRecurrence &&
    (!recurrenceAvailabilityPreview?.reviewedPlanToken ||
      (!hasPersistedRequestSeries && !hasIncludedRecurrenceOccurrence));
  const previewSeriesRequestCount = pendingRecurrence
    ? recurrenceAvailabilityPreview?.included.length ?? null
    : null;
  const seriesRequestCount = (() => {
    if (pendingRecurrence) {
      if (previewSeriesRequestCount !== null) {
        return previewSeriesRequestCount;
      }

      return 0;
    }

    if (
      booking?.__typename === "StandardBooking" &&
      booking.seriesCount &&
      booking.seriesCount > 1
    ) {
      return booking.seriesCount;
    }

    return 1;
  })();

  const hasPaymentCardOnFile = !!booking?.paymentCardOnFile?.id;
  const requirePaymentCardOnFile =
    booking?.paymentRequirements?.requirePaymentCardOnFile ?? false;
  const requiresPrePayment =
    !isPatronRecurringRequest &&
    booking?.paymentRequirements?.prePaymentRequirement === "FULL";
  const enrollInLoyalty =
    requiresPrePayment &&
    !!patronLoyalty &&
    isBookingOwner &&
    hasLoyaltyEnrollmentOffer &&
    (loyaltyEnrollmentPreference?.preference ?? false);
  const grossOrderTotal =
    booking?.orders?.edges?.reduce(
      (total, edge) =>
        total +
        (edge?.node?.status === "FULLY_PAID" ? 0 : edge?.node?.total ?? 0),
      0
    ) ?? 0;
  const orderTotal = booking?.totalDueAfterCredits ?? grossOrderTotal;
  const requiresPaymentMethod =
    requirePaymentCardOnFile || (requiresPrePayment && orderTotal !== 0);
  const shouldOpenPaymentMethodModal =
    requiresPaymentMethod && !hasPaymentCardOnFile && bookingId !== undefined;

  const handleOpenNudgeModalAfterCompletedBooking =
    useOpenNudgeModalAfterCompletedBooking(hasPublicSeenNotificationNudgeModal);

  const handleOpenConfirmedBookingAfterCompletedBooking =
    useOpenConfirmedBookingModalAfterCompletedBooking();
  const handleOpenPostConfirmationModal = useCallback(
    (isStandaloneRequest = false) => {
      if (isRequestBooking || hasPublicSeenNotificationNudgeModal) {
        handleOpenConfirmedBookingAfterCompletedBooking(
          pendingRecurrence && !isStandaloneRequest
            ? "recurring-request-received"
            : "default"
        );
      } else {
        handleOpenNudgeModalAfterCompletedBooking();
      }
    },
    [
      handleOpenConfirmedBookingAfterCompletedBooking,
      handleOpenNudgeModalAfterCompletedBooking,
      hasPublicSeenNotificationNudgeModal,
      isRequestBooking,
      pendingRecurrence,
    ]
  );

  const arrivedFromFulfillmentFlow = booking?.replacesBookingId !== null;

  const [confirmDraftBooking, isConfirmingDraftBooking, requestErrors] =
    useConfirmDraftBooking(
      membershipTypeId,
      isLessonBooking ? "LESSON" : "STANDARD",
      requestGroupCount
    );
  const [confirmDraftBookings, isConfirmingDraftBookings] =
    useConfirmDraftBookings(
      membershipTypeId,
      isLessonBooking ? "LESSON" : "STANDARD",
      (bookingIds?.length ?? 0) > 1 ? bookingIds?.[0] ?? "" : "",
      isRequestBooking
    );
  const [submitPatronRequestSeries, isSubmittingSeries] =
    useSubmitPatronRequestSeries(membershipTypeId);
  const [upsertPatronRequestSeries, isUpsertingSeries] =
    useUpsertPatronRequestSeries();

  const isNarrow = useIsBookingSummaryNarrow();
  const [isApplePayProcessing, setIsApplePayProcessing] = useState(false);

  const { roundRestrictions, groupRestrictions } =
    useGetBookingRestrictionStatuses({
      fragmentRef: booking ?? null,
    });

  const hasViolatingRestrictions =
    roundRestrictions.some((round) =>
      round.some(
        (status) =>
          status?.willViolate && status?.rule?.memberOverride === "NONE"
      )
    ) ||
    groupRestrictions.some(
      (status) => status?.willViolate && status?.rule?.memberOverride === "NONE"
    );

  const hasBlockingRequestRestrictionStatus =
    useHasBlockingRequestRestrictionStatus(booking ?? null);

  const isConfirmDisabled =
    (!isPatronRecurringRequest && hasViolatingRestrictions) ||
    (!isPatronRecurringRequest && booking?.requestSubmissionBlocked) ||
    hasBlockingRequestRestrictionStatus ||
    isPendingRecurrenceSubmissionDisabled;

  const handleDismissClick = useCallback(() => {
    if (!arrivedFromFulfillmentFlow) {
      return;
    }

    unlockAllSlots(() => {
      if (currentRequestBookingId) {
        navigation.navigate("BookingSummary", {
          slotOrBookingId: currentRequestBookingId,
          params: {},
        });
      }
    });
  }, [
    arrivedFromFulfillmentFlow,
    unlockAllSlots,
    navigation,
    currentRequestBookingId,
  ]);

  const clearPendingRecurrence = useCallback(() => {
    navigation.setParams({
      params: {
        ...route.params?.params,
        pendingRecurrence: undefined,
      },
    });
  }, [navigation, route.params?.params]);

  const pendingRecurrenceInput = useMemo<PatronRequestRecurrenceInput | null>(
    () =>
      pendingRecurrence?.endCondition === "ON_DATE"
        ? {
            recurrenceType: pendingRecurrence.recurrenceType,
            repeatInterval: pendingRecurrence.repeatInterval,
            endCondition: "ON_DATE",
            endDate: pendingRecurrence.endDate,
          }
        : pendingRecurrence
        ? {
            recurrenceType: pendingRecurrence.recurrenceType,
            repeatInterval: pendingRecurrence.repeatInterval,
            endCondition: "AFTER_OCCURRENCES",
            occurrenceCount: pendingRecurrence.occurrenceCount ?? 1,
          }
        : null,
    [pendingRecurrence]
  );

  const trackConfirmationEvent = useCallback(() => {
    if (arrivedFromFulfillmentFlow) {
      trackEvent(
        "BookingDetail.BookingReservationConfirmButtonClickedFromFulfillmentFlow",
        {
          requestBookingId: currentRequestBookingId ?? "",
        }
      );
    }
  }, [arrivedFromFulfillmentFlow, trackEvent, currentRequestBookingId]);

  const confirmPendingRecurrence = useCallback(() => {
    const reviewedPlanToken = recurrenceAvailabilityPreview?.reviewedPlanToken;

    if (
      !hasPendingConfirmedRecurrence ||
      !bookingId ||
      !pendingRecurrenceInput ||
      !reviewedPlanToken ||
      isPendingRecurrenceSubmissionDisabled
    ) {
      return;
    }

    upsertPatronRequestSeries(
      bookingId,
      pendingRecurrenceInput,
      (isStandaloneRequest) => {
        clearPendingRecurrence();
        handleOpenConfirmedBookingAfterCompletedBooking(
          isStandaloneRequest
            ? "default"
            : hasPersistedRequestSeries
            ? "recurring-request-updated"
            : "recurring-request-received"
        );
      },
      { reviewedPlanToken },
      { suppressSuccessToast: true }
    );
  }, [
    bookingId,
    clearPendingRecurrence,
    handleOpenConfirmedBookingAfterCompletedBooking,
    hasPendingConfirmedRecurrence,
    hasPersistedRequestSeries,
    isPendingRecurrenceSubmissionDisabled,
    pendingRecurrenceInput,
    recurrenceAvailabilityPreview,
    upsertPatronRequestSeries,
  ]);

  const facilityId = booking?.startSlot?.agenda?.facility?.id;
  const handleApplePayNonce = useCallback(
    (paymentNonce: string) =>
      new Promise<void>((resolve, reject) => {
        if (!bookingId) {
          reject(new Error(t`The booking could not be found.`));
          return;
        }

        confirmDraftBooking(
          {
            bookingId,
            paymentNonce,
            ...(patronLoyalty ? { enrollInLoyalty } : {}),
          },
          resolve,
          isRequestBooking,
          reject
        );
      }),
    [
      bookingId,
      confirmDraftBooking,
      enrollInLoyalty,
      isRequestBooking,
      patronLoyalty,
    ]
  );
  const handleApplePayError = useCallback(
    (error: Error) => {
      showToast({
        description: error.message || t`Unable to process Apple Pay.`,
        semanticStyle: "error",
        toastId: "booking-apple-pay-error",
      });
    },
    [showToast]
  );
  const showApplePayButton =
    applePay &&
    requiresPrePayment &&
    booking?.paymentRequirements?.squareAvailable &&
    !isMultipleBooking &&
    orderTotal > 0 &&
    !!facilityId;

  const proceedWithConfirm = useCallback(() => {
    if (shouldOpenPaymentMethodModal) {
      setShouldModalBeOpen(true);
    } else if (bookingId) {
      if (pendingRecurrence && isDraftBooking && pendingRecurrenceInput) {
        const reviewedPlanToken =
          recurrenceAvailabilityPreview?.reviewedPlanToken;
        if (isPendingRecurrenceSubmissionDisabled || !reviewedPlanToken) return;
        submitPatronRequestSeries(
          {
            sourceBookingId: bookingId,
            recurrence: pendingRecurrenceInput,
            reviewedPlanToken,
          },
          (isStandaloneRequest) => {
            handleOpenPostConfirmationModal(isStandaloneRequest);
            clearPendingRecurrence();
          }
        );
        return;
      }

      if (isMultipleBooking) {
        confirmDraftBookings(
          {
            bookingIds: bookingIds ?? [bookingId],
          },
          handleOpenPostConfirmationModal
        );
      } else {
        const confirmationInput = {
          bookingId,
          ...(patronLoyalty ? { enrollInLoyalty } : {}),
        };

        confirmDraftBooking(
          confirmationInput,
          handleOpenPostConfirmationModal,
          isRequestBooking
        );
      }
    }
  }, [
    bookingId,
    clearPendingRecurrence,
    confirmDraftBooking,
    setShouldModalBeOpen,
    shouldOpenPaymentMethodModal,
    handleOpenPostConfirmationModal,
    isRequestBooking,
    confirmDraftBookings,
    isMultipleBooking,
    bookingIds,
    isPendingRecurrenceSubmissionDisabled,
    pendingRecurrence,
    pendingRecurrenceInput,
    isDraftBooking,
    submitPatronRequestSeries,
    recurrenceAvailabilityPreview,
    enrollInLoyalty,
    patronLoyalty,
  ]);

  const handleConfirmClick = useCallback(() => {
    if (onRequestConfirm) {
      trackConfirmationEvent();
      onRequestConfirm(proceedWithConfirm);
      return;
    }

    proceedWithConfirm();
  }, [onRequestConfirm, proceedWithConfirm, trackConfirmationEvent]);

  return showConfirmAction || hasPendingConfirmedRecurrence ? (
    <VStack
      padding={5}
      backgroundColor="grayscale-0"
      borderRadius={isNarrow ? "0" : "4"}
      maxWidth={BOOKING_SUMMARY_CONTENT_MAX_WIDTH}
      width="100%"
      {...(isMultipleBooking && {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        marginLeft: "auto",
        marginRight: "auto",
      })}
    >
      {showConfirmAction ? (
        <HStack justifyContent="center">
          <SlotLockCountdownTimer
            agendaId={booking?.startSlot?.agenda?.id}
            currentServerTime={currentServerTime}
            lockedUntil={booking?.startSlot?.lockedUntil ?? null}
            timeSlotId={booking?.startSlot?.id}
            onExpireCallback={
              arrivedFromFulfillmentFlow ? handleDismissClick : handleGoBack
            }
          />
        </HStack>
      ) : null}

      <AggregateRequestErrorBanner
        errors={requestErrors ?? []}
        requestGroupCount={requestGroupCount}
      />

      {hasPendingConfirmedRecurrence ? (
        <HStack marginTop={5} gap={4}>
          <HStack width={120}>
            <Button
              accessibilityLabel={t`Cancel recurring request changes`}
              onPress={clearPendingRecurrence}
              testID="cancel-recurring-request-changes-button"
              variant="secondary"
            >{t`Cancel`}</Button>
          </HStack>
          <Button
            onPress={confirmPendingRecurrence}
            isLoading={isUpsertingSeries}
            isDisabled={isPendingRecurrenceSubmissionDisabled}
            testID="booking-reservation-button"
            accessibilityLabel={t`Booking Reservation Button`}
            style={{ flex: 1 }}
          >
            {t`Submit Changes`}
          </Button>
        </HStack>
      ) : arrivedFromFulfillmentFlow ? (
        <HStack marginTop={5} gap={4}>
          <HStack width={120}>
            <Button
              accessibilityLabel={t`Dismiss button`}
              onPress={handleDismissClick}
              testID="dismiss-button"
              variant="secondary"
            >{t`Dismiss`}</Button>
          </HStack>
          <Button
            onPress={handleConfirmClick}
            isLoading={
              isSubmittingSeries ||
              isConfirmingDraftBooking ||
              isConfirmingDraftBookings ||
              isUpsertingSeries
            }
            isDisabled={isConfirmDisabled}
            testID="booking-reservation-button"
            accessibilityLabel={t`Booking Reservation Button`}
            style={{ flex: 1 }}
          >
            {pendingRecurrence && !recurrenceAvailabilityPreview
              ? t`Submit Requests`
              : getButtonText(
                  hasPaymentCardOnFile,
                  !!requirePaymentCardOnFile,
                  requiresPrePayment,
                  clubSupportsPrivateBookings,
                  isLessonBooking,
                  isRequestBooking,
                  orderTotal,
                  seriesRequestCount
                )}
          </Button>
        </HStack>
      ) : null}

      {!arrivedFromFulfillmentFlow && !hasPendingConfirmedRecurrence ? (
        <HStack paddingTop={5} width="100%">
          <Button
            onPress={handleConfirmClick}
            isLoading={
              isSubmittingSeries ||
              isConfirmingDraftBooking ||
              isConfirmingDraftBookings ||
              isUpsertingSeries
            }
            isDisabled={isConfirmDisabled}
            testID="booking-reservation-button"
            accessibilityLabel={t`Booking Reservation Button`}
            width="100%"
          >
            {pendingRecurrence && !recurrenceAvailabilityPreview
              ? t`Submit Requests`
              : getButtonText(
                  hasPaymentCardOnFile,
                  !!requirePaymentCardOnFile,
                  requiresPrePayment,
                  clubSupportsPrivateBookings,
                  isLessonBooking,
                  isRequestBooking,
                  orderTotal,
                  seriesRequestCount
                )}
          </Button>
        </HStack>
      ) : null}
      {showApplePayButton ? (
        <FacilityApplePayButton
          facilityId={facilityId}
          bookingId={bookingId}
          amount={orderTotal}
          isDisabled={
            isConfirmDisabled ||
            isApplePayProcessing ||
            isConfirmingDraftBooking ||
            isConfirmingDraftBookings
          }
          label={t`Whoosh booking`}
          onError={handleApplePayError}
          onNonce={handleApplePayNonce}
          onProcessingChange={setIsApplePayProcessing}
          onSuccess={handleOpenPostConfirmationModal}
        />
      ) : null}
    </VStack>
  ) : null;
};

const BookingReservationFragmentString = graphql`
  fragment BookingReservation_Booking on Booking
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    paymentRequirements {
      requirePaymentCardOnFile
      prePaymentRequirement
      squareAvailable
    }
    replacesBookingId
    requestSubmissionBlocked
    id
    __typename
    orders {
      edges {
        node {
          status
          total
        }
      }
    }
    totalDueAfterCredits
    paymentCardOnFile {
      id
    }
    bookingSeries {
      kind
    }
    ... on StandardBooking {
      status
      seriesCount
      patronOrders: orders(first: 1) {
        edges {
          node {
            loyaltyEnrollmentOffer @include(if: $patronLoyalty) {
              points
            }
          }
        }
      }
    }
    ... on LessonBooking {
      status
      patronOrders: orders(first: 1) {
        edges {
          node {
            loyaltyEnrollmentOffer @include(if: $patronLoyalty) {
              points
            }
          }
        }
      }
    }
    ...useGetBookingRestrictionStatusesFragment
    ...useRequestRestrictionStatuses_booking
    startSlot {
      id
      type
      ... on TimeSlot {
        lockedUntil
      }

      agenda {
        date
        id
        facility {
          id
          club {
            supportsPrivate
          }
        }
      }
    }
  }
`;
